import React, { useCallback, useEffect, useState } from 'react'
import { ConfigurationContext, useAppProviderContext } from '../../core/application/contexts'
import { ISearchTerm } from '../../core/application/contexts/ConfigurationContext'
import { DropOffType, TenantConfiguration, WasteType } from '../../core/domain/models'
import { IDropOffTypeProvider } from '../../core/domain/providers/DropOffTypeProvider'
import { IWasteTypeProvider } from '../../core/domain/providers/WasteTypeProvider'

export interface IConfigurationManagerProps {
  children?: React.ReactNode
  tenantId?: string
}

const ConfigurationManager: React.FC<IConfigurationManagerProps> = ({ tenantId, children }) => {
  const { wasteTypeProvider, tenantProvider, dropOffTypeProvider } = useAppProviderContext()
  const [wasteTypeList, setWasteTypeList] = useState<Array<WasteType>>([])
  const [tenantWasteTypeList, setTenantWasteTypeList] = useState<Array<WasteType>>([])
  const [dropoffTypeList, setDropOffTypeList] = useState<Array<DropOffType>>([])
  const [mostPopularSearches, setMostPopularSearches] = useState<Array<ISearchTerm>>([])
  const [tenantConfiguration, setTenantConfiguration] = useState<TenantConfiguration>()

  useEffect(() => {
    const fetchWasteTypeList = async (provider: IWasteTypeProvider) => {
      try {
        const data = await provider.fetchAvailableWasteType()
        setWasteTypeList(data)
      } catch (err) {
        console.error(err)
      }
    }
    if (wasteTypeProvider) {
      fetchWasteTypeList(wasteTypeProvider)
    }
  }, [wasteTypeProvider])

  useEffect(() => {
    const fetchDropOffTypes = async (provider: IDropOffTypeProvider) => {
      try {
        const data = await provider.fetchAvailableDropOffTypes()
        setDropOffTypeList(data)
      } catch (err) {
        console.error(err)
      }
    }
    if (dropOffTypeProvider) {
      fetchDropOffTypes(dropOffTypeProvider)
    }
  }, [dropOffTypeProvider])

  const fetchTenantConfiguration = useCallback(async () => {
    if (tenantProvider && tenantId) {
      try {
        const data = await tenantProvider.fetchTenantConfiguration(tenantId)
        setTenantConfiguration(data)
        setMostPopularSearches([...data.mostPopularTags, ...data.mostPopularWasteTypes])
      } catch (err) {
        console.error(err)
      }
    }
  }, [tenantProvider, tenantId])

  useEffect(() => {
    if (wasteTypeList.length > 0 && tenantConfiguration?.wasteTypeIds && tenantConfiguration?.wasteTypeIds.length > 0) {
      const tenantWasteTypes: Array<WasteType> = wasteTypeList.filter((wasteType: WasteType) =>
        tenantConfiguration.wasteTypeIds.includes(wasteType.id)
      )
      if (tenantWasteTypes.length > 0) {
        setTenantWasteTypeList(tenantWasteTypes)
      }
    }
  }, [tenantConfiguration, wasteTypeList])

  useEffect(() => {
    if (tenantProvider && tenantId) {
      fetchTenantConfiguration()
    }
  }, [tenantId, tenantProvider, fetchTenantConfiguration])

  return (
    <ConfigurationContext.Provider
      value={{
        tenantId,
        setWasteTypeList,
        mostPopularSearches,
        wasteTypeList: tenantWasteTypeList.length > 0 ? tenantWasteTypeList : wasteTypeList,
        tenantConfiguration,
        setTenantConfiguration,
        dropoffTypeList
      }}
    >
      {children}
    </ConfigurationContext.Provider>
  )
}

export default ConfigurationManager
